import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Download,
  Filter,
  Layers,
  Store,
  RefreshCcw,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Printer as PrintIcon,
  Tag,
  ArrowUpDown,
  Archive,
  EyeOff,
  DollarSign,
  Package,
  XOctagon,
  BarChart3,
  PieChart,
  TrendingUp,
} from "lucide-react";
import ReactApexChart from "react-apexcharts";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";

import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";

export const StockReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "Rs.";

  const [filters, setFilters] = useState(() => ({
    category_id: "",
    store_id: currentStore?.id ? String(currentStore.id) : "",
    include_inactive: 0,
    low_stock_only: 0,
    sort_by: "product_name",
    sort_order: "asc",
  }));
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCategorySearch(categorySearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [categorySearch]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // Detect dark mode from localStorage
  useEffect(() => {
    const checkDarkMode = () => {
      const colorScheme = localStorage.getItem("colorschema");
      setIsDarkMode(colorScheme === "dark_mode");
    };

    checkDarkMode();
    const interval = setInterval(checkDarkMode, 500);
    window.addEventListener("storage", checkDarkMode);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkDarkMode);
    };
  }, []);

  // ==================== QUERIES ====================
  const { data: categories = [], isFetching: isCategoriesFetching } = useQuery({
    enabled: hasPermission("report product stock view"),
    queryKey: ["categories", debouncedCategorySearch],
    queryFn: async () => {
      const params = {};
      if (debouncedCategorySearch) {
        params.search = debouncedCategorySearch;
      }
      const response = await apiClient.get(`/category_product`, {
        params,
      });
      return response.data.data;
    },
  });

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report product stock view"),
    queryKey: ["stores"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data;
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report product stock view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.include_inactive) params.include_inactive = 1;
      if (currentFilters.low_stock_only) params.low_stock_only = 1;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.STOCK_REPORT,
        {
          params,
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report product stock view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.include_inactive) params.include_inactive = 1;
      if (currentFilters.low_stock_only) params.low_stock_only = 1;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.STOCK_REPORT,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Stock report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to generate stock report",
        "error",
      );
    },
  });

  const handleGenerateReport = () => {
    showToast("Generating stock report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      category_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      include_inactive: 0,
      low_stock_only: 0,
      sort_by: "product_name",
      sort_order: "asc",
    });
  };

  const previewRows = React.useMemo(
    () => previewData?.data || [],
    [previewData],
  );

  const dashboardStats = React.useMemo(() => {
    if (!previewData) return null;

    const summary = previewData.summary || {};
    let outOfStockCount = 0;
    let inStockCount = 0;
    let lowStockCount = 0;

    const storeValues = {};

    previewRows.forEach((row) => {
      const qty = row.quantity || 0;
      const cost = row.product?.cost || 0;
      const reorder = row.reorder_level || 0;

      if (qty <= 0) {
        outOfStockCount++;
      } else if (qty <= reorder) {
        lowStockCount++;
      } else {
        inStockCount++;
      }

      const storeName = row.store?.name || "Unassigned";
      if (!storeValues[storeName]) storeValues[storeName] = 0;
      if (qty > 0) storeValues[storeName] += qty * cost;
    });

    const statusLabels = ["In Stock", "Low Stock", "Out of Stock"];
    const statusSeries = [inStockCount, lowStockCount, outOfStockCount];
    const statusColors = ["#22c55e", "#f59e0b", "#ef4444"];

    const sortedStores = Object.entries(storeValues).sort(
      (a, b) => b[1] - a[1],
    );
    const storeLabels = sortedStores.map((s) => s[0]);
    const storeSeries = sortedStores.map((s) => s[1]);

    return {
      // Prefer backend-computed totals; client tallies are used for chart breakdowns only
      totalValue: summary.total_value ?? 0,
      totalProducts: summary.total_products ?? previewRows.length,
      lowStockCount: summary.low_stock_count ?? lowStockCount,
      outOfStockCount,
      currency: currencySymbol,
      statusPieOptions: {
        chart: { type: "donut", background: "transparent" },
        labels: statusLabels,
        theme: { mode: isDarkMode ? "dark" : "light" },
        plotOptions: { pie: { donut: { size: "70%" } } },
        dataLabels: { enabled: false },
        colors: statusColors,
        stroke: { show: false },
        legend: { position: "bottom" },
      },
      statusPieSeries: statusSeries,
      storeBarOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          background: "transparent",
        },
        xaxis: {
          categories: storeLabels,
          labels: { style: { cssClass: "text-xs text-muted-foreground" } },
        },
        yaxis: {
          labels: {
            formatter: (val) =>
              `${currencySymbol}${val >= 1000 ? (val / 1000).toFixed(1) + "k" : val}`,
          },
        },
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: "45%", distributed: true },
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        theme: { mode: isDarkMode ? "dark" : "light" },
      },
      storeBarSeries: [{ name: "Inventory Value", data: storeSeries }],
    };
  }, [previewData, isDarkMode, currencySymbol, previewRows]);

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `stock-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove the iframe after the print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const categoryOptions = categories.map((category) => ({
    value: String(category.id),
    label: category.name,
  }));

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <Archive className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Inventory Logistics
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Product Stock Report
              </span>
            </div>
          }
          subtitle="Monitor inventory levels across all locations. Identify low-stock items and track product availability."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            className="rounded-xl mr-2"
            onClick={handleClearFilters}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report product stock view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report product stock view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div
        className={
          showFilters ? "grid lg:grid-cols-3 gap-4" : "flex flex-col gap-4"
        }
      >
        <div className={showFilters ? "lg:col-span-2 space-y-4" : "space-y-4"}>
          <Card className="border border-border/50 shadow-none overflow-hidden transition-all duration-300">
            <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-background shadow-sm rounded-lg border">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <CardTitle className="text-lg">Filtering Criteria</CardTitle>
                  <CardDescription className="text-xs text-muted-foreground">
                    Limit results to specific locations or types
                  </CardDescription>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="text-xs font-semibold rounded-lg"
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </CardHeader>
            {showFilters && (
              <CardContent className="p-5">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Store className="h-4 w-4 text-primary" /> Location Scope
                    </h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          Warehouse / Store
                        </Label>
                        <ReactSelect
                          options={storeOptions}
                          value={
                            storeOptions.find(
                              (opt) => opt.value === String(filters.store_id || ""),
                            ) ||
                            (filters.store_id &&
                              currentStore?.id &&
                              String(filters.store_id) === String(currentStore.id)
                              ? {
                                value: String(currentStore.id),
                                label: currentStore.name,
                              }
                              : null)
                          }
                          onChange={(opt) =>
                            setFilters((p) => ({
                              ...p,
                              store_id: opt?.value || "",
                            }))
                          }
                          placeholder="All Locations"
                          isClearable={Boolean(filters.store_id)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Tag className="h-4 w-4 text-primary" /> Categorization
                    </h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          Product Category
                        </Label>
                        <ReactSelect
                          options={categoryOptions}
                          isLoading={isCategoriesFetching}
                          value={categoryOptions.find(
                            (opt) => opt.value === String(filters.category_id),
                          )}
                          onInputChange={(val, { action }) => {
                            if (action === "input-change") {
                              setCategorySearch(val);
                            }
                          }}
                          onChange={(opt) =>
                            setFilters((p) => ({
                              ...p,
                              category_id: opt?.value || "",
                            }))
                          }
                          placeholder="All Categories"
                          isClearable
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t space-y-4">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2 mb-4">
                    <ArrowUpDown className="h-4 w-4 text-primary" /> Output
                    Sequencing
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Sort Rows By
                      </Label>
                      <Select
                        value={filters.sort_by}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, sort_by: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="product_name">
                            Product Name
                          </SelectItem>
                          <SelectItem value="sku">SKU Code</SelectItem>
                          <SelectItem value="quantity">
                            Current Balance
                          </SelectItem>
                          <SelectItem value="reorder_level">
                            Reorder Point
                          </SelectItem>
                          <SelectItem value="store_name">
                            Store Hierarchy
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Sorting Direction
                      </Label>
                      <Select
                        value={filters.sort_order}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, sort_order: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asc">
                            Ascending (A-Z, 0-9)
                          </SelectItem>
                          <SelectItem value="desc">
                            Descending (Z-A, 9-0)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {showFilters && (
          <div className="space-y-4">
            <Card className="border border-border/50 shadow-none transition-all duration-300">
              <CardHeader className="bg-muted/50 border-b pb-4 pt-4">
                <CardTitle className="text-lg">
                  Visibility & Thresholds
                </CardTitle>
                <CardDescription className="text-muted-foreground text-xs">
                  Special state filters
                </CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-dashed transition-colors group cursor-pointer hover:border-primary/50">
                  <div className="space-y-0.5">
                    <Label className="group-hover:text-primary font-bold cursor-pointer transition-colors">
                      Low Stock Only
                    </Label>
                    <p className="text-[10px] text-muted-foreground">
                      Show items below reorder level
                    </p>
                  </div>
                  <Switch
                    checked={!!filters.low_stock_only}
                    onCheckedChange={(c) =>
                      setFilters((p) => ({ ...p, low_stock_only: c ? 1 : 0 }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-dashed transition-colors group cursor-pointer hover:border-primary/50">
                  <div className="space-y-0.5">
                    <Label className="group-hover:text-primary font-bold cursor-pointer transition-colors">
                      Inc. Inactive
                    </Label>
                    <p className="text-[10px] text-muted-foreground">
                      Show items marked as inactive
                    </p>
                  </div>
                  <Switch
                    checked={!!filters.include_inactive}
                    onCheckedChange={(c) =>
                      setFilters((p) => ({ ...p, include_inactive: c ? 1 : 0 }))
                    }
                  />
                </div>

                <div className="p-5 rounded-2xl bg-primary/5 border border-primary/10 mt-6 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
                    <AlertTriangle className="h-16 w-16 text-primary" />
                  </div>
                  <h4 className="text-primary font-bold text-sm mb-2 relative z-10">
                    Need an Export?
                  </h4>
                  <p className="text-primary/70 text-[11px] leading-relaxed relative z-10">
                    This report generates a high-fidelity PDF optimized for
                    physical inventory audits and signatures.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {previewData && dashboardStats && (
        <div className="flex flex-col gap-8 mt-8 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-none shadow-lg ring-1 ring-border/50">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Total Inventory Value
                  </p>
                  <h3 className="text-2xl font-bold text-foreground">
                    {dashboardStats.currency}
                    {dashboardStats.totalValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </h3>
                </div>
                <div className="p-3 bg-primary/10 text-primary rounded-xl">
                  <DollarSign className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg ring-1 ring-border/50">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Total Products
                  </p>
                  <h3 className="text-2xl font-bold text-foreground">
                    {dashboardStats.totalProducts.toLocaleString()}
                  </h3>
                </div>
                <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                  <Package className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg ring-1 ring-border/50">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Low Stock Items
                  </p>
                  <h3 className="text-2xl font-bold text-foreground">
                    {dashboardStats.lowStockCount}
                  </h3>
                </div>
                <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                  <AlertTriangle className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg ring-1 ring-border/50">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Out of Stock
                  </p>
                  <h3 className="text-2xl font-bold text-foreground">
                    {dashboardStats.outOfStockCount}
                  </h3>
                </div>
                <div className="p-3 bg-red-100 text-red-600 rounded-xl">
                  <XOctagon className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="lg:col-span-2 shadow-xl border-none ring-1 ring-border/50 overflow-hidden">
              <CardHeader className="bg-muted/30 border-b pb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg font-bold">
                    Inventory Value by Store
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ReactApexChart
                  options={dashboardStats.storeBarOptions}
                  series={dashboardStats.storeBarSeries}
                  type="bar"
                  height={300}
                />
              </CardContent>
            </Card>

            <Card className="shadow-xl border-none ring-1 ring-border/50 overflow-hidden">
              <CardHeader className="bg-muted/30 border-b pb-4">
                <div className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg font-bold">
                    Stock Status
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6 flex items-center justify-center min-h-[300px]">
                {dashboardStats.statusPieSeries.reduce((a, b) => a + b, 0) >
                  0 ? (
                  <ReactApexChart
                    options={dashboardStats.statusPieOptions}
                    series={dashboardStats.statusPieSeries}
                    type="donut"
                    height={300}
                  />
                ) : (
                  <p className="text-muted-foreground">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Compiled!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Your stock inventory report has been successfully generated
                  and is ready for download or printing.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};
